import { CacheStrategy } from '@rammetec/data-access';
import { AUDIT_FIELD_DESCRIPTORS, AUDIT_FOREIGN_KEYS } from './_auditFields.js';
import { BUSINESS_ALLOWED_DATA_SOURCES, BUSINESS_DEFAULT_DATA_SOURCE } from './dataSourcePolicy.js';
import { PURCHASE_ORDER_ITEM } from '../types/purchase-order-item.js';
/**
 * Descriptor for the purchase order line item (PurchaseOrder child entity).
 * Subcollection: org/{orgId}/purchase-order/{purchaseOrderId}/item
 *
 * Resolve repository with context: { orgId, purchaseOrderId }.
 * Owned child — no lifecycle; audit via AUDIT_* (request 0235).
 */
export const purchaseOrderItemDescriptor = {
    name: PURCHASE_ORDER_ITEM,
    label: 'Item da ordem de compra',
    domain: 'purchasing',
    collectionPath: 'org/{orgId}/purchase-order/{purchaseOrderId}/item',
    pathPatternKeys: {
        orgId: 'string',
        purchaseOrderId: 'string',
    },
    version: 1,
    description: 'Purchase order line item referencing a Product, with computed totalPrice',
    repositoryConfig: {
        dataSource: BUSINESS_DEFAULT_DATA_SOURCE,
        // NoCache: filho de entidade versionada (spec 0045) — itens em cache voltariam velhos após Recarregar e seriam regravados por cima.
        cache: CacheStrategy.NoCache,
    },
    allowedDataSources: BUSINESS_ALLOWED_DATA_SOURCES,
    fields: [
        { name: 'id', label: 'ID', type: 'string', required: true, description: 'Unique item identifier' },
        {
            name: 'purchaseOrderId',
            label: 'Ordem de compra',
            type: 'string',
            required: true,
            description: 'Owning purchase order id (also encoded in the subcollection path)',
        },
        {
            name: 'productId',
            label: 'Produto',
            type: 'string',
            required: true,
            description: 'Reference to the product (ProductRef.productId)',
        },
        {
            name: 'variantId',
            label: 'Variante',
            type: 'string',
            required: false,
            description: 'Partial ProductVariant (identifying axes; ProductRef.variantId). Size lives on PurchaseOrderItemBreakdown.',
        },
        {
            name: 'description',
            example: 'Fio PV 30/1 cru',
            label: 'Descrição',
            type: 'string',
            required: true,
            description: 'Line description (snapshot of the product description at purchase time)',
        },
        {
            name: 'quantity',
            example: 300,
            label: 'Quantidade',
            type: 'number',
            required: true,
            description: 'Quantity ordered',
        },
        {
            name: 'enteredQuantity',
            example: 300,
            label: 'Quantidade digitada',
            type: 'number',
            required: false,
            description: 'Quantity as typed in an alternate product unit (memory; base quantity remains authoritative)',
        },
        {
            name: 'enteredUnitId',
            label: 'Unidade digitada',
            type: 'string',
            required: false,
            description: 'FK ProductUnit used when typing enteredQuantity (spec 0050 D4)',
        },
        {
            name: 'receivedQuantity',
            example: 300,
            label: 'Recebido',
            type: 'number',
            required: true,
            description: 'Qty received across posted goods receipts (service-owned, 0109)',
            defaultValue: 0,
        },
        {
            name: 'unitPrice',
            example: 22.5,
            label: 'Custo unitário',
            type: 'number',
            required: true,
            description: 'Unit cost at purchase time',
        },
        {
            name: 'totalPrice',
            example: 6750,
            label: 'Total',
            type: 'number',
            required: false,
            description: 'Computed = quantity * unitPrice (service invariant)',
        },
        ...AUDIT_FIELD_DESCRIPTORS,
    ],
    foreignKeys: [
        {
            fieldName: 'productId',
            targetEntity: 'Product',
            relationship: 'many-to-one',
            displayField: 'description',
            description: 'Produto vinculado ao item da ordem',
        },
        {
            fieldName: 'variantId',
            targetEntity: 'ProductVariant',
            relationship: 'many-to-one',
            displayField: 'description',
            description: 'Variante parcial (identificadores; sem tamanho)',
        },
        {
            fieldName: 'enteredUnitId',
            targetEntity: 'ProductUnit',
            relationship: 'many-to-one',
            displayField: 'description',
            description: 'Unidade do produto usada na digitação (memória)',
        },
        {
            fieldName: 'purchaseOrderId',
            targetEntity: 'PurchaseOrder',
            relationship: 'many-to-one',
            displayField: 'code',
            description: 'Ordem de compra de origem',
        },
        ...AUDIT_FOREIGN_KEYS,
    ],
};
//# sourceMappingURL=purchase-order-item.descriptor.js.map